// frontend/src/components/Layout.js
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import NotificationCenter from './NotificationCenter';
import NotificationBar from './NotificationBar';
import MobileBottomNav, { BOTTOM_NAV_HEIGHT } from './MobileBottomNav';
import PageTransition from './PageTransition';
import BackToTopFab from './BackToTopFab';
import UiFeedbackWidget from './UiFeedbackWidget';
import { CurrentPageContext } from '../contexts/CurrentPageContext';
import { useCurrentPage, labelForNavPath } from '../hooks/useCurrentPage';
import GlobalCommandPalette from './GlobalCommandPalette';
import { useCommands } from '../contexts/CommandRegistry';
import ShortcutCheatsheet, { useShortcutCheatsheet } from './ShortcutCheatsheet';
import { buildNewNavSections, OLD_TO_NEW_GROUP_KEY, NAV_FLAG_KEY } from './navSections';
import { useRecentPagesTracker, useRecentPages } from '../hooks/useRecentPages';
import { APP_VERSION_LABEL, formatBuildDate } from '../utils/appVersion';
import { useMediaQuery, useTheme } from '@mui/material';
import { navSidebarPaperProps, navSectionHeaderColor, navResizeHoverColor, navLogoStyle } from './layout/navSidebarSx';
import HistoryIcon from '@mui/icons-material/History';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  Collapse,
  Tooltip } from
'@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Share as ShareIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  CloudQueue as CloudIcon,
  Security as SecurityIcon,
  IntegrationInstructions as IntegrationIcon,
  Help as HelpIcon,
  Lightbulb as LightbulbIcon,
  Code as CodeIcon,
  Videocam as VideocamIcon,
  Business as BusinessIcon,
  Engineering as EngineeringIcon,
  Storefront as StorefrontIcon,
  ReceiptLong as ReceiptLongIcon,
  Extension as ExtensionIcon,
  RequestQuote as RequestQuoteIcon,
  Inventory2 as CatalogManagementIcon,
  SystemUpdate as FirmwareIcon,
  LocalHospital as AlarmIcon,
  Event as EventIcon,
  Celebration as CelebrationIcon,
  Map as MapIcon,
  Apps as AppsIcon,
  Devices as DevicesIcon,
  Monitor as MonitorIcon,
  Troubleshoot as TroubleshootIcon,
  Link as LinkIcon,
  Router as RouterIcon,
  Category as CategoryIcon,
  People as PeopleIcon,
  RadioButtonChecked as ButtonIcon,
  Assignment as InspectionIcon,
  PowerSettingsNew as PowerIcon,
  ExpandLess,
  ExpandMore,
  AccountTree as AccountTreeIcon,
  SmartToy as AutomationIcon,
  AutoFixHigh as BulkNameIcon,
  Phone as PhoneIcon,
  TabletMac as EinkIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FiberManualRecord as PresenceDot,
  Keyboard as KeyboardIconImport,
  DensityMedium as DensityComfortableIcon,
  DensitySmall as DensityCompactIcon } from
'@mui/icons-material';
import BugReportIcon from '@mui/icons-material/BugReport';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { visibilityPermissionForRoute } from '@caro/shared/constants';
import { useModules } from '@caro/shared/modules';
import { useThemeSettings } from "../contexts/ThemeContext";
import SiteFilterSelector from './SiteFilterSelector';
import { axiosInstance, documentsApi } from '../services/apiClient';

const defaultDrawerWidth = 240;
const railWidth = 72;
const MIN_DRAWER_WIDTH = 200;
const MAX_DRAWER_WIDTH = 420;
const RAIL_PREF_KEY = 'caro.portal.drawerRail';
const WIDTH_PREF_KEY = 'caro.portal.drawerWidthResolved';

const Layout = () => {const { t, themeMode, setThemeMode, density, setDensity } = useThemeSettings();
  // Navigation sidebar styling from theme.caro.sidebar (spec 20260930-caro-editorial-theme):
  // aubergine under Caro Editorial, today's glass look (nothing added) under every other theme.
  const muiTheme = useTheme();
  const navPaperProps = navSidebarPaperProps(muiTheme);
  const presence = muiTheme.caro?.legacy?.presence;
  const navigate = useNavigate();
  const location = useLocation();
  const dynamicCommands = useCommands();
  const { user, logout, isImpersonating } = useAuth();
  const { canManageUsers, canViewUsers, hasPermission } = usePermissions();
  // Paid module entitlements (spec 20260818-invoicingsystem). A nav item for a module
  // the organisation has NOT bought must not appear at all — the server would 403 it
  // with MODULE_NOT_ACTIVE, and an item that is always refused is worse than absent.
  const { canRead: canReadModule, canWrite: canWriteModule } = useModules();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);

  // Drawer expanded-width — user can drag the right edge to resize the
  // wide drawer between MIN and MAX. Persisted, so setup survives reloads.
  const [drawerWidthResolved, setDrawerWidthResolved] = useState(() => {
    try {
      const raw = localStorage.getItem(WIDTH_PREF_KEY);
      const n = raw ? parseInt(raw, 10) : NaN;
      if (!Number.isNaN(n) && n >= MIN_DRAWER_WIDTH && n <= MAX_DRAWER_WIDTH) return n;
    } catch { /* ignore */ }
    return defaultDrawerWidth;
  });

  // Resize drag state. We pointer-capture on the handle so the user can
  // drag over the main content without losing the grip mid-motion.
  const [resizing, setResizing] = useState(false);
  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (e) => {
      const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      const next = Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, x));
      setDrawerWidthResolved(next);
    };
    const onUp = () => {
      setResizing(false);
      try { localStorage.setItem(WIDTH_PREF_KEY, String(drawerWidthResolved)); } catch {}
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
    // Prevent text selection while dragging.
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [resizing, drawerWidthResolved]);

  // Drawer rail mode: collapsed to 72px icon-only on tablet-ish widths
  // by default, expanded to full on large screens. User can force the
  // opposite via localStorage. Mobile is unaffected (always uses the
  // temporary hamburger drawer).
  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const [railMode, setRailMode] = useState(() => {
    try {
      const saved = localStorage.getItem(RAIL_PREF_KEY);
      if (saved === 'rail') return true;
      if (saved === 'wide') return false;
    } catch { /* ignore */ }
    return false; // will be set by the sync effect below on first paint
  });
  useEffect(() => {
    try {
      const saved = localStorage.getItem(RAIL_PREF_KEY);
      if (saved) return; // user picked something, honour it
    } catch { /* ignore */ }
    setRailMode(!isLargeScreen);
  }, [isLargeScreen]);
  const toggleRail = () => {
    setRailMode((prev) => {
      const next = !prev;
      try { localStorage.setItem(RAIL_PREF_KEY, next ? 'rail' : 'wide'); } catch {}
      return next;
    });
  };

  // Hover-to-peek: when the drawer is in rail mode, hovering over it
  // temporarily expands it (without flipping the persistent preference).
  // Navigation via the peek collapses back automatically. A pointer-fine
  // media query ensures touch devices don't accidentally trigger the
  // peek (they don't hover in the meaningful sense).
  const hasFinePointer = useMediaQuery('(pointer: fine)');
  const [peekOpen, setPeekOpen] = useState(false);
  const peekCloseTimer = useRef(null);
  const openPeek = () => {
    if (!railMode || !hasFinePointer) return;
    if (peekCloseTimer.current) {
      clearTimeout(peekCloseTimer.current);
      peekCloseTimer.current = null;
    }
    setPeekOpen(true);
  };
  const schedulePeekClose = () => {
    if (peekCloseTimer.current) clearTimeout(peekCloseTimer.current);
    // Small grace period so moving the pointer through a tooltip/submenu
    // doesn't flicker the drawer shut.
    peekCloseTimer.current = setTimeout(() => setPeekOpen(false), 150);
  };
  const closePeekImmediate = () => {
    if (peekCloseTimer.current) {
      clearTimeout(peekCloseTimer.current);
      peekCloseTimer.current = null;
    }
    setPeekOpen(false);
  };
  useEffect(() => () => { if (peekCloseTimer.current) clearTimeout(peekCloseTimer.current); }, []);
  // Any time the user leaves rail mode manually, cancel any peek.
  useEffect(() => { if (!railMode) closePeekImmediate(); }, [railMode]);

  // Effective rail state for rendering: when peeking, the drawer looks
  // wide even though the persistent preference is still "rail".
  const effectiveRail = railMode && !peekOpen;
  const currentDrawerWidth = effectiveRail ? railWidth : drawerWidthResolved;

  // New-nav is now the default. Three-state flag:
  //   - missing / unset → new nav (default for first-time visits)
  //   - '1'             → new nav (explicit opt-in, same result)
  //   - '0'             → classic nav (explicit opt-out)
  // That lets users who prefer the old grouping keep it, while
  // everyone else benefits from the reorganisation.
  const [newNav, setNewNav] = useState(() => {
    try { return localStorage.getItem(NAV_FLAG_KEY) !== '0'; }
    catch { return true; }
  });
  const toggleNewNav = () => {
    setNewNav((prev) => {
      const next = !prev;
      try { localStorage.setItem(NAV_FLAG_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Global command palette (Cmd/Ctrl+K) and `?` cheatsheet.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const cheatsheet = useShortcutCheatsheet();

  // Cmd/Ctrl+B flips the sidebar between rail (icon-only) and wide.
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && !e.shiftKey && (e.key === 'b' || e.key === 'B')) {
        const tag = e.target?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
        e.preventDefault();
        toggleRail();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleRail]); // eslint-disable-line react-hooks/exhaustive-deps

  // `G` then a destination key jumps to that page — matches the
  // cheatsheet promise. Pattern: press G (prime), then press H / A /
  // T / L / N / P / D within 1.2s. Any other key cancels the chord.
  // Ignored while typing in inputs or with modifier keys held.
  useEffect(() => {
    const DESTS = {
      h: '/dashboard',
      a: '/alarms',
      t: '/tickets',
      l: '/locations',
      n: '/notifications',
      p: '/persons',
      d: '/devices',
    };
    let primed = false;
    let timer = null;
    const cancel = () => {
      primed = false;
      if (timer) { clearTimeout(timer); timer = null; }
    };
    const onKey = (e) => {
      const t = e.target;
      const tag = t?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (primed) {
        cancel();
        const target = DESTS[k];
        if (target && target !== location.pathname) {
          e.preventDefault();
          navigate(target);
        }
        return;
      }
      if (k === 'g') {
        primed = true;
        timer = setTimeout(cancel, 1200);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); cancel(); };
  }, [navigate, location.pathname]);

  // Track visited pages for the palette's "Recent" section. We feed
  // the label-lookup from the existing nav data so the recent entries
  // show friendly names ("Alarms") rather than raw paths ("/alarms").
  // One label source (20260925-ui-feedback, plan T1.24): the same labelForNavPath the
  // document.title effect and the UI-feedback widget use.
  const labelForPath = React.useCallback((p) => {
    return labelForNavPath(flatNavItems(), p) || p;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useRecentPagesTracker(labelForPath);
  const recentPages = useRecentPages();
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Cmd/Ctrl+. opens (or focuses) the UI-feedback panel (20260925-ui-feedback, V-11).
  // Deliberately NOT ignored inside inputs: you may want to remark on a form while filling
  // it in, and Mod+. types no character. A no-op for users without the feedback button
  // (the widget renders nothing, so the ref stays null).
  const feedbackRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod || e.shiftKey || e.altKey) return;
      if (e.key !== '.' && e.code !== 'Period') return;
      if (!feedbackRef.current) return;
      e.preventDefault();
      feedbackRef.current.open();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Sync document.title with the active nav label so tab switchers
  // read "Alarms — CARO" rather than just "CARO".
  useEffect(() => {
    const label = labelForNavPath(flatNavItems(), location.pathname);
    document.title = label ? `${label} — CARO` : 'CARO Management Controller';
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Browser online/offline awareness for the presence dot.
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  // Load company settings on mount
  useEffect(() => {
    const loadCompanySettings = async () => {
      try {
        const response = await axiosInstance.get('/company/settings');
        if (response.data.success) {
          setCompanySettings(response.data.data.settings);
        }
      } catch (error) {
        console.error('Error loading company settings:', error);
        // Fail silently - settings will be null
      }
    };

    if (user) {
      loadCompanySettings();
    }
  }, [user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Click handler for nav links. They render as real <a href> (RouterLink)
  // so right-click → "Open in new tab", Ctrl/Cmd-click and middle-click all
  // work natively. On a plain left-click we let the Link navigate and only
  // run our side effects (close the mobile drawer, collapse the rail peek).
  // For modifier/aux clicks we bail early so opening a new tab leaves the
  // current view untouched.
  const handleNavClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    setMobileOpen(false);
    if (railMode && peekOpen) closePeekImmediate();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Per-item permission booleans. hasPermission() short-circuits to true for
  // admin / company_admin via FULL_ACCESS_ROLES, so admin-tier users keep
  // seeing every menu item without an explicit `adminOnly` flag.
  const cameraRequestsPermission = hasPermission('camera_requests.view');
  // Company setting controls feature enablement, but menu should be visible
  // for users with permission.
  const cameraSettingEnabled = companySettings?.cameraFootageRequestsEnabled !== false;
  const fieldInterventionsPermission = hasPermission('field_interventions.view');
  // /company-management is CARO-ONLY (spec 20260819-registrationandonboarding/spec-02,
  // LD-CM-SPLIT). This used to also admit company.settings.manage / company.edit, which is
  // how a customer's own company_admin reached a page where EVERY call 403s: the routes
  // behind it sit under requireAdmin() (routes/companies.js:21), a hard role check that
  // never consults checkPermission, so the FULL_ACCESS_ROLES bypass buys nothing there.
  //
  // The customer's equivalent is /organisation, below — backed by /api/company/settings,
  // which is permission-gated per company and therefore actually works for them.
  const companyManagementPermission = hasPermission('system.admin');
  const organisationPermission = hasPermission('company.settings.manage');
  // Customer onboarding queue. PROTECTED key — no role grants it implicitly, not even
  // `admin`; see specs/20260819-registrationandonboarding/PERMISSIONS-GRANT.md. An empty
  // menu here is a missing grant, not a broken deploy.
  const onboardingViewPermission = hasPermission('onboarding.view');

  // Top-level item visibility — keys live in @caro/shared/constants.
  //
  // Only the entries that need to OVERRIDE the registry are computed here.
  // personsPermission / ticketsPermission / alarmsPermission / notificationsPermission
  // used to sit in this block, were never attached to their menu items, and so gated
  // nothing at all while making the file read as if they did. isItemVisible() now
  // derives those from PAGE_REGISTRY, which is also what App.js's route guards are
  // audited against — so there is nothing left to keep in step by hand.
  //
  // Dashboard stays an explicit `|| true`: it is the landing page, and a user who
  // cannot see its menu entry has nowhere to land.
  const dashboardPermission = hasPermission('dashboard.view') || true;
  const locationsPermission = hasPermission('locations.view');
  const devicesPermission = hasPermission('devices.view');
  // Shop / device catalog (spec 20260702-repair-estimates-catalog). Every logged-in
  // role gets catalog.view by default; admin/manager also get catalog.manage.
  const shopPermission = hasPermission('catalog.view');
  const catalogManagePermission = hasPermission('catalog.manage');
  // Permission templates (spec 20260824-permission-templates). Either key opens the
  // page: `templates.manage` edits the CARO-global baselines (PROTECTED, named
  // accounts only), `templates.edit` edits your own company's overrides. Someone
  // holding neither has nothing to do there, so the entry stays hidden.
  const permissionTemplatesPermission =
    hasPermission('templates.manage') || hasPermission('templates.edit');
  // PROTECTED and cross-tenant: the orphan-location screen spans every customer.
  const locationTenancyPermission = hasPermission('locations.tenant_repair');
  // Database server health (2026-09-18). CARO-only the way /firmware-management is: the key
  // is NOT PROTECTED (CARO admins hold it automatically), the company-1 check is what keeps
  // a customer's company_admin from seeing an item the API would 403.
  const dbHealthPermission =
    hasPermission('system.db_health.view')
    && Number(user?.company?.id || user?.companyId || user?.company_id) === 1;
  // E-mail quarantine: CARO-only (company 1) — the API gates it with requireCaroCompany.
  const ticketQuarantinePermission =
    hasPermission('tickets.view')
    && Number(user?.company?.id || user?.companyId || user?.company_id) === 1;
  // Estimates overview (2026-07-17 increment) — same read right as in-ticket estimates.
  const estimatesPermission = hasPermission('estimates.view');
  // Knowledge Base: reading is ungated (any role); the authoring console is
  // gated on knowledge_base.manage (spec 20260706-portal-knowledge-base §8.2).
  const knowledgeBaseManagePermission = hasPermission('knowledge_base.manage');
  // Portal Ideabox (spec 20260817-portal-ideabox). `ideas.view` is a role default for
  // every portal role; `ideas.triage`/`ideas.manage` are PROTECTED and gate the
  // Triage tab inside the page, not this nav item.
  const ideasPermission = hasPermission('ideas.view');
  // Documentclassificatie (spec 20260902-documentclassificatie, T-5a.35 / PD-9). The /documents
  // landing page opens for ANY of the seven documents keys the registry row declares: the
  // classify work list for `documents.classify`, the tiles for the six topic readers. Reading a
  // file is never a key (D-14) — this only decides whether the menu entry is shown.
  const documentsPermission = ['documents.classify', 'documents.read.medical', 'documents.read.financial', 'documents.read.legal', 'documents.read.technical', 'documents.read.housing', 'documents.read.hr']
    .some((key) => hasPermission(key));
  // 5b (T-5b.19): the two PROTECTED admin pages. PermissionContext honours PROTECTED_PERMISSIONS, so an
  // admin-tier role without the explicit grant gets `false` here — and 403 from the API.
  const documentsGrantsPermission = hasPermission('documents.access_admin');
  const documentsExclusionsPermission = hasPermission('documents.exclusion_admin');
  // 5c (T-5c.14): the archiving page and its nav badge — the number of documents past their proposed
  // retention term. PROTECTED key; the badge is fetched ONLY when the caller holds it, so an account
  // without `documents.retention` makes no call to /documents/archive/badge at all (spec §9, H4.2).
  const documentsRetentionPermission = hasPermission('documents.retention');
  const [documentsArchiveBadge, setDocumentsArchiveBadge] = useState(0);
  const inDocumentsArea = location.pathname.startsWith('/documents');
  useEffect(() => {
    if (!documentsRetentionPermission) { setDocumentsArchiveBadge(0); return undefined; }
    let cancelled = false;
    const loadBadge = async () => {
      try {
        const res = await documentsApi.archive.badge();
        if (!cancelled) setDocumentsArchiveBadge(Number(res?.data?.due) || 0);
      } catch (err) {
        // A failed badge is not an error the user can act on; the page itself says what is wrong.
        if (!cancelled) setDocumentsArchiveBadge(0);
      }
    };
    loadBadge();
    // Refresh on navigation back into the documents area (a purge changes the count), not on a timer.
    return () => { cancelled = true; };
  }, [documentsRetentionPermission, inDocumentsArea]);

  // ── Paid modules (spec 20260818-invoicingsystem) ──────────────────────────
  //
  // `hasPermission` here comes from PermissionContext, which DOES honour
  // PROTECTED_PERMISSIONS — unlike hooks/usePermission.js, which returns true for every
  // key when the role is admin/company_admin. That distinction matters exactly here:
  // `modules.manage` is PROTECTED, so an admin WITHOUT an explicit grant correctly sees
  // no Modules item, which is what the API will also tell them.
  const modulesManagePermission = hasPermission('modules.manage');
  const modulesViewPermission = hasPermission('modules.view');

  // Invoicing surfaces need BOTH the permission and the module. `canRead` rather than
  // `canWrite` on the read-only surfaces so a customer whose module was switched off
  // keeps reaching records they are legally required to retain (D27).
  const invoicingConfigPermission =
    hasPermission('invoicing.config') && canWriteModule('invoicing');
  const invoicingViewPermission =
    hasPermission('invoicing.view') && canReadModule('invoicing');
  const invoicingPaymentsPermission =
    hasPermission('invoicing.payments') && canWriteModule('invoicing');

  // The POS / till. Same two-gate rule as invoicing, and the same read/write split: the
  // report and the day closes stay reachable after the module lapses because they are the
  // bookkeeping record of money that was actually taken (D27); everything that CHANGES the
  // cafeteria needs the module to be live.
  const posCatalogPermission = hasPermission('pos.catalog') && canWriteModule('pos');
  const posReportsPermission = hasPermission('pos.reports') && canReadModule('pos');
  const posRestrictionsPermission =
    hasPermission('pos.restrictions') && canWriteModule('pos');
  const posTerminalsPermission = hasPermission('pos.terminals') && canWriteModule('pos');
  const posDayClosePermission = hasPermission('pos.cash_count') && canReadModule('pos');

  // Bottom-group item visibility.
  const personTypeManagePermission = hasPermission('persontypes.manage') || canManageUsers;
  const deviceTypeManagePermission = hasPermission('device_types.manage');
  const deviceLinkingPermission = hasPermission('device_linking.manage');
  const firmwarePermission = hasPermission('firmware.view') || hasPermission('firmware.create') || hasPermission('firmware.edit') || hasPermission('firmware.delete');
  const enoceanViewPermission = hasPermission('enocean.view');
  const enoceanImportPermission = hasPermission('enocean.import');
  const locationDataImportPermission = hasPermission('locations.manage');
  const unitAuditPermission = hasPermission('unit_audit.view');
  const qbusManagePermission = hasPermission('qbus.manage');
  const qbusEditPermission = hasPermission('qbus.edit') || hasPermission('qbus.manage');
  const automationsViewPermission = hasPermission('automations.view');
  const baseipPermission = hasPermission('baseip.manage');
  const indoorMappingPermission = hasPermission('indoor.manage');
  const telephonyViewPermission = hasPermission('telephony.view');
  const integrationsManagePermission = hasPermission('integrations.manage');
  const einkManagePermission = hasPermission('eink.manage');
  const servicesManagePermission = hasPermission('services.manage');
  const monitoringViewPermission = hasPermission('monitoring.view');
  const systemAdminPermission = hasPermission('system.admin');

  //console.log('[Layout] Menu visibility:', {
  //  cameraRequestsPermission,
  //  cameraSettingEnabled,
  //  fieldInterventionsPermission,
  //  companySettings,
  //  companyManagementPermission,
  //  userRole: user?.role
  //});

  const menuItems = [
  { text: t ? t('Layout.menu_dashboard') : 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', requiresPermission: dashboardPermission },
  { text: t ? t('Layout.menu_locations') : 'Locations', icon: <LocationOnIcon />, path: '/locations', requiresPermission: locationsPermission },
  { text: t ? t('Layout.menu_devices') : 'Devices', icon: <CloudIcon />, path: '/devices', requiresPermission: devicesPermission },
  { text: t ? t('Layout.menu_device_health') : 'Device Health', icon: <MonitorIcon />, path: '/devices/health', requiresPermission: devicesPermission },
  // Camera's (spec 20260831-cameras-als-toestel). Staat ook in `navSections.js` onder
  // Hardware, maar dát menu wordt alleen gebruikt als de `newNav`-vlag aan staat. Wie de
  // schakelaar uitzet kreeg de pagina hier niet te zien en concludeerde dat hij ontbrak.
  // Zolang er twee menu's bestaan, moet een ingang in beide staan.
  //
  // ⚠ `adminOnly` BOVENOP de sleutel uit PAGE_REGISTRY (`cameras.view`).
  //
  // Camerabeheer is CARO-werk: het gaat over RTSP-adressen, inloggegevens van klant-
  // apparatuur en welke appartementen mogen meekijken. Beslissing van de eigenaar op
  // 2026-09-03 — niet voor gewone gebruikers.
  //
  // `isItemVisible()` leest dit als `user.role !== 'admin'` ⇒ verbergen, dus dit sluit
  // OOK `company_admin` uit, terwijl die de permissie via FULL_ACCESS_ROLES wel houdt.
  // De pagina is dan onbereikbaar via het menu maar de route blijft open op de sleutel;
  // wil je dat óók dicht, dan hoort dat in de routebewaking van App.js, niet hier.
  { text: t ? t('Layout.menu_cameras') : "Camera's", icon: <VideocamIcon />, path: '/cameras', adminOnly: true },
  // Persons, Tickets, Alarms, Notifications are always visible to any
  // authenticated user — matches legacy behavior. Granular gating on these
  // would require a permissions backfill migration on existing user rows
  // (whose `permissions.default` only carries the bare 5-key seed). Until
  // that ships, these items stay open. See Option B 2026-04-29.
  // Agenda (spec 20260825-animatie-activiteiten-en-agenda §8). No
  // requiresPermission: isItemVisible() derives it from PAGE_REGISTRY
  // (/agenda -> agenda.view), so the menu and the route guard read the same list.
  //
  // agenda.view is in NO role default and in NO permission template, so this entry
  // is invisible to every existing customer until someone grants the key on purpose
  // (CLAUDE.md, closed by default). That includes CARO super_admins — super_admin is
  // in CROSS_ORG_ROLES but NOT in FULL_ACCESS_ROLES, so it inherits no permission at
  // all (spec F-4).
  { text: t ? t('Layout.menu_agenda') : 'Agenda', icon: <EventIcon />, path: '/agenda' },
  // Activities (PAID module). No requiresPermission: isItemVisible() derives
  // activities.view from PAGE_REGISTRY. The MODULE gate is inside the page, so an
  // entitled-but-unpermitted user never sees the entry and a permitted user of a
  // company without the module sees the entry and a clear explanation — which is the
  // right way round: 'you lack a right' and 'your organisation has not bought this'
  // are different conversations.
  { text: t ? t('Layout.menu_activities') : 'Activities', icon: <CelebrationIcon />, path: '/activities' },
  { text: t ? t('Layout.menu_persons') : 'Persons', icon: <PeopleIcon />, path: '/persons' },
  // ── Documentclassificatie (spec 20260902-documentclassificatie) ────────
  //
  // Stond alleen in `navSections.js`, dus onzichtbaar voor iedereen die de nieuwe
  // zijbalk niet aan heeft staan. Zelfde soort gat als bij Camera's.
  //
  // Geen `requiresPermission` op de landingspagina zelf zou hier fout zijn: de
  // registry-rij noemt zeven sleutels en heeft géén read-verb, dus
  // `visibilityPermissionForRoute` zou de eerste pakken (`documents.classify`) en de
  // pagina verbergen voor de zes topic-lezers waarvoor hij óók bestaat.
  { text: t ? t('Layout.menu_documents') : 'Documenten', icon: <FolderIcon />, path: '/documents', requiresPermission: documentsPermission },
  // De twee PROTECTED beheerschermen. Ze zijn onderling onverenigbaar (§7.4): één
  // account ziet er terecht hoogstens één van de twee.
  { text: t ? t('Layout.menu_documents_grants') : 'Documenttoegang', icon: <FolderIcon />, path: '/documents/grants', requiresPermission: documentsGrantsPermission },
  { text: t ? t('Layout.menu_documents_exclusions') : 'Documentuitsluitingen', icon: <FolderIcon />, path: '/documents/exclusions', requiresPermission: documentsExclusionsPermission },
  // De badge is het aantal documenten voorbij hun bewaartermijn, en wordt ALLEEN
  // opgehaald als de beller `documents.retention` heeft (§9, H4.2).
  { text: t ? t('Layout.menu_documents_archive') : 'Archivering', icon: <FolderIcon />, path: '/documents/archive', requiresPermission: documentsRetentionPermission, badge: documentsArchiveBadge || null, badgeColor: 'error' },
  { text: t ? t('Layout.menu_tickets') : 'Tickets', icon: <BugReportIcon />, path: '/tickets' },
  { text: t ? t('Layout.menu_ticket_quarantine') : 'E-mail Quarantine', icon: <MarkEmailUnreadIcon />, path: '/tickets/quarantine', requiresPermission: ticketQuarantinePermission },
  { text: t ? t('Layout.menu_alarms') : 'Alarms', icon: <AlarmIcon />, path: '/alarms' },
  { text: t ? t('Layout.menu_notifications') : 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
  { text: t ? t('Layout.menu_camera_requests') : 'Camera Requests', icon: <VideocamIcon />, path: '/camera-requests', requiresPermission: cameraRequestsPermission },
  { text: t ? t('Layout.menu_field_interventions') : 'Field Interventions', icon: <EngineeringIcon />, path: '/field-interventions' },
  { text: t ? t('Layout.menu_shop') : 'Shop', icon: <StorefrontIcon />, path: '/shop', requiresPermission: shopPermission },
  { text: t ? t('Layout.menu_catalog_management') : 'Catalog Management', icon: <CatalogManagementIcon />, path: '/catalog-management', requiresPermission: catalogManagePermission },
  { text: t ? t('Layout.menu_estimates') : 'Estimates', icon: <RequestQuoteIcon />, path: '/estimates', requiresPermission: estimatesPermission },
  // `canViewUsers`, not `canManageUsers`. This entry named the permission-matrix right
  // alone, so someone granted `users.manage` -- which the matrix on this very page offers
  // against /users -- got no menu entry and a redirect from the route. The three keys
  // PAGE_REGISTRY declares for /users now open it, and what each one REACHES is decided
  // inside the page (spec 20260826-company-user-management LD-3).
  { text: t ? t('Layout.menu_user_management') : 'User management', icon: <SecurityIcon />, path: '/users', requiresPermission: canViewUsers },
  { text: t ? t('Layout.menu_permission_templates') : 'Permission templates', icon: <SecurityIcon />, path: '/permission-templates', requiresPermission: permissionTemplatesPermission },
  { text: t ? t('Layout.menu_location_tenancy') : 'Tenant assignment', icon: <SecurityIcon />, path: '/admin/location-tenancy', requiresPermission: locationTenancyPermission },
  // No requiresPermission: isItemVisible() derives it from PAGE_REGISTRY
  // (/audit -> audit.view), so the menu and the route guard read the same list.
  { text: t ? t('Layout.menu_audit') : 'Audit trail', icon: <SecurityIcon />, path: '/audit' }];

  // Grouped bottom menu items
  const bottomMenuGroups = [
    // ── Paid modules (spec 20260818-invoicingsystem) ───────────────────────
    //
    // These were ten FLAT top-level entries, which buried the rest of the menu under two
    // features. They are groups now.
    //
    // A group renders only when at least one of its items is permitted, and both flags per
    // item already fold in the module check — so a customer without the module, or a user
    // without the rights, sees no group at all rather than an empty expander.
    {
      id: 'invoicing',
      label: t ? t('Layout.group_invoicing') : 'Facturatie',
      icon: <ReceiptLongIcon />,
      items: [
        { text: t ? t('Layout.menu_invoicing') : 'Facturen', icon: <ReceiptLongIcon />, path: '/invoicing', requiresPermission: invoicingViewPermission },
        { text: t ? t('Layout.menu_invoicing_items') : 'Te factureren', icon: <ReceiptLongIcon />, path: '/invoicing/items', requiresPermission: invoicingViewPermission },
        { text: t ? t('Layout.menu_invoicing_payments') : 'Betalingen', icon: <ReceiptLongIcon />, path: '/invoicing/payments', requiresPermission: invoicingPaymentsPermission },
        { text: t ? t('Layout.menu_invoicing_config') : 'Instellingen', icon: <ReceiptLongIcon />, path: '/invoicing/config', requiresPermission: invoicingConfigPermission },
      ],
    },
    {
      id: 'cafeteria',
      label: t ? t('Layout.group_cafeteria') : 'Kassa',
      icon: <StorefrontIcon />,
      items: [
        // Six routes, ONE tabbed screen: each entry opens its own tab. The split exists so
        // someone who may only count the cash sees exactly that one item, instead of a
        // catalog editor they would be refused at.
        { text: t ? t('Layout.menu_pos_catalog') : 'Catalogus', icon: <StorefrontIcon />, path: '/pos/catalog', requiresPermission: posCatalogPermission },
        { text: t ? t('Layout.menu_pos_config') : 'Kassa-instellingen', icon: <StorefrontIcon />, path: '/pos/config', requiresPermission: posCatalogPermission },
        { text: t ? t('Layout.menu_pos_restrictions') : 'Productbeperkingen', icon: <StorefrontIcon />, path: '/pos/restrictions', requiresPermission: posRestrictionsPermission },
        { text: t ? t('Layout.menu_pos_terminals') : 'Kassa-terminals', icon: <StorefrontIcon />, path: '/pos/terminals', requiresPermission: posTerminalsPermission },
        { text: t ? t('Layout.menu_pos_day_closes') : 'Dagafsluitingen', icon: <StorefrontIcon />, path: '/pos/day-closes', requiresPermission: posDayClosePermission },
        { text: t ? t('Layout.menu_pos_reports') : 'Kassarapport', icon: <StorefrontIcon />, path: '/pos/reports', requiresPermission: posReportsPermission },
      ],
    },
    // ── Plaatsbeschrijvingen (BETALENDE module `inspections`) ─────────────
    //
    // Spec 20260901-bewoner-onboarding. De vier routes bestaan sinds fase 14 en staan in
    // PAGE_REGISTRY, maar stonden in GEEN van beide menu's — ze waren dus alleen te
    // bereiken door het adres te typen. Zolang er twee menu's zijn moet een ingang in
    // allebei staan; zie de opmerking bij Camera's hierboven, waar precies dit misging.
    //
    // GEEN `requiresPermission`: `isItemVisible()` leest de sleutel uit PAGE_REGISTRY
    // (/inspections → `inspections.view`, /inspections/templates →
    // `inspections.templates`). Eén bron, dus het menu en de routebewaking in App.js
    // kunnen niet uit elkaar lopen.
    //
    // De MODULE-poort zit in de pagina zelf, niet hier — net als bij Activiteiten. "Jij
    // mist een recht" en "jouw organisatie heeft dit niet gekocht" zijn twee verschillende
    // gesprekken, en alleen het eerste hoort een menu-ingang te laten verdwijnen.
    //
    // De twee detailroutes (/inspections/:uuid en /:uuid/capture) horen hier niet: je
    // navigeert ernaartoe vanuit een rapport, nooit vanuit een menu.
    {
      id: 'inspections',
      label: t ? t('Layout.group_inspections') : 'Condition reports',
      icon: <InspectionIcon />,
      items: [
        { text: t ? t('Layout.menu_inspections') : 'Overview', icon: <InspectionIcon />, path: '/inspections' },
        { text: t ? t('Layout.menu_inspection_templates') : 'Templates', icon: <InspectionIcon />, path: '/inspections/templates' },
      ],
    },
    {
      id: 'management',
      label: t ? t('Layout.group_management') : 'Management',
      icon: <BusinessIcon />,
      items: [
        { text: t ? t('Layout.menu_send_notifications') : 'Send Notifications', icon: <NotificationsActiveIcon />, path: '/notification-management', requiresPermission: hasPermission('notifications.manage') || canManageUsers },
        { text: t ? t('Layout.menu_company_management') : 'Company Management', icon: <BusinessIcon />, path: '/company-management', requiresPermission: companyManagementPermission },
        { text: t ? t('Layout.menu_organisation') : 'Organisation', icon: <BusinessIcon />, path: '/organisation', requiresPermission: organisationPermission },
        { text: t ? t('Layout.menu_onboarding') : 'Onboarding', icon: <BusinessIcon />, path: '/admin/onboarding', requiresPermission: onboardingViewPermission },
        { text: t ? t('Layout.menu_person_type_management') : 'Person Type Management', icon: <CategoryIcon />, path: '/person-type-management', requiresPermission: personTypeManagePermission },
        // ── Alleen in het moderne menu tot 2026-09-09 ──────────────────────
        //
        // Zeven schermen uit spec 20260819-registrationandonboarding die in dit menu
        // ontbraken. Alle zeven zonder `requiresPermission`: `isItemVisible()` leest de
        // sleutel uit PAGE_REGISTRY, dezelfde lijst waartegen de routebewaking in App.js
        // wordt getoetst.
        { text: t ? t('Layout.menu_branding') : 'Huisstijl', icon: <BusinessIcon />, path: '/branding' },
        // Intrede en uittrede staan naast elkaar omdat ze één levenscyclus zijn — en
        // zolang uittreden geen eigen ingang had, was hard verwijderen het enige gebaar
        // dat iemand kon maken.
        { text: t ? t('Layout.menu_resident_onboarding') : 'Bewoner laten intrekken', icon: <CategoryIcon />, path: '/persons/onboard' },
        { text: t ? t('Layout.menu_resident_offboarding') : 'Bewoner laten uittrekken', icon: <CategoryIcon />, path: '/persons/offboard' },
        { text: t ? t('Layout.menu_checklist_templates') : 'Intrede-checklist', icon: <CategoryIcon />, path: '/checklist-templates' },
        { text: t ? t('Layout.menu_intakes') : 'Intakegesprekken', icon: <CategoryIcon />, path: '/intake' },
        { text: t ? t('Layout.menu_intake_forms') : 'Intakeformulieren', icon: <CategoryIcon />, path: '/intake-forms' },
        { text: t ? t('Layout.menu_print_profiles') : 'Afdrukken naar derden', icon: <CategoryIcon />, path: '/print-profiles' },
        { text: t ? t('Layout.menu_candidate_cleanup') : 'Kandidaten opruimen', icon: <CategoryIcon />, path: '/candidate-cleanup' },
      ],
    },
    {
      id: 'device-management',
      label: t ? t('Layout.group_device_management') : 'Device Management',
      icon: <DevicesIcon />,
      adminOnly: true,
      items: [
        { text: t ? t('Layout.menu_device_type_management') : 'Device Type Management', icon: <DevicesIcon />, path: '/device-type-management', adminOnly: true },
        { text: t ? t('Layout.menu_tablet_permissions') : 'Tablet Permissions', icon: <DevicesIcon />, path: '/tablet-permissions', adminOnly: true },
        { text: t ? t('Layout.menu_device_linking') : 'Device Linking', icon: <LinkIcon />, path: '/device-linking', adminOnly: true },
        { text: t ? t('Layout.menu_firmware_management') : 'Firmware Management', icon: <FirmwareIcon />, path: '/firmware-management', adminOnly: true },
      ],
    },
    {
      id: 'integrations',
      label: t ? t('Layout.group_integrations') : 'Integrations',
      icon: <AccountTreeIcon />,
      items: [
        { text: t ? t('Layout.menu_enocean_buttons') : 'EnOcean Buttons', icon: <ButtonIcon />, path: '/enocean-buttons', adminOnly: true },
        { text: t ? t('Layout.menu_enocean_import') : 'EnOcean Import', icon: <ButtonIcon />, path: '/enocean-import', adminOnly: true },
        { text: t ? t('Layout.menu_location_data_import') : 'Location Data Import', icon: <LocationOnIcon />, path: '/location-data-import', adminOnly: true },
        // Gate comes from PAGE_REGISTRY (/location-phone-numbers -> locations.phone_numbers).
        { text: t ? t('Layout.menu_location_phone_numbers') : 'Location phone numbers', icon: <LocationOnIcon />, path: '/location-phone-numbers' },
        { text: t ? t('Layout.menu_unit_audit') : 'Unit Audit', icon: <LocationOnIcon />, path: '/unit-audit', adminOnly: true },
        { text: t ? t('Layout.menu_qbus_output_types') : 'Qbus Output Types', icon: <PowerIcon />, path: '/qbus-output-types', adminOnly: true },
        { text: t ? t('Layout.menu_automations') : 'Automations', icon: <AutomationIcon />, path: '/automations', adminOnly: true },
        { text: t ? t('Layout.menu_qbus_friendly_names') : 'Qbus Friendly Names', icon: <BulkNameIcon />, path: '/qbus-friendly-names', adminOnly: true },
        { text: t ? t('Layout.menu_qbus_monitor') : 'Qbus Monitor', icon: <MonitorIcon />, path: '/qbus-monitor', adminOnly: true },
        { text: t ? t('Layout.menu_qbus_prefixes') : 'Domotica naamclassificatie', icon: <PowerIcon />, path: '/qbus-prefixes', adminOnly: true },
        // Meldingsgeluiden (spec 20260831-domotica-meldingen-bewonerstablet, fase 2).
        // Geen `adminOnly`: de zichtbaarheid komt uit PAGE_REGISTRY
        // (/notification-sounds -> resident_notifications.view).
        { text: t ? t('Layout.menu_notification_sounds') : 'Meldingsgeluiden', icon: <NotificationsIcon />, path: '/notification-sounds' },
        { text: t ? t('Layout.menu_baseip_management') : 'BaseIP Management', icon: <RouterIcon />, path: '/baseip-management', adminOnly: true },
        { text: t ? t('Layout.menu_indoor_mapping') : 'Indoor Mapping', icon: <MapIcon />, path: '/indoor-mapping', adminOnly: true },
        { text: t ? t('Layout.menu_telephony') : 'Telephony', icon: <PhoneIcon />, path: '/telephony', adminOnly: true },
        { text: t ? t('Layout.menu_integration_management') : 'Integration Management', icon: <IntegrationIcon />, path: '/integration-management', adminOnly: true },
        { text: t ? t('Layout.menu_eink_designer') : 'E-Ink Designer', icon: <EinkIcon />, path: '/eink-designer', adminOnly: true },
      ],
    },
    {
      id: 'system',
      label: t ? t('Layout.group_system') : 'System',
      icon: <SettingsIcon />,
      items: [
        // Paid module entitlements (spec 20260818-invoicingsystem spec-00 §6).
        //
        // requiresPermission, NOT adminOnly: isItemVisible() reads adminOnly as
        // `role === 'admin'` exactly, and `modules.manage` is a PROTECTED permission that
        // the admin role does NOT inherit. With adminOnly this would appear for every
        // admin and 403 on every call — visible to the wrong people and useless to them.
        { text: t ? t('Layout.menu_modules_admin') : 'Modules (CARO)', icon: <ExtensionIcon />, path: '/admin/modules', requiresPermission: modulesManagePermission },
        { text: t ? t('Layout.menu_my_modules') : 'Mijn modules', icon: <ExtensionIcon />, path: '/settings/modules', requiresPermission: modulesViewPermission },
        { text: t ? t('Layout.menu_service_management') : 'Service Management', icon: <AppsIcon />, path: '/service-management', adminOnly: true },
        { text: t ? t('Layout.menu_monitoring') : 'Monitoring', icon: <MonitorIcon />, path: '/monitoring', adminOnly: true },
        // Observability feedback loop (20260807-observability-feedback-loop §7). Sits directly
        // under Monitoring: same permission, adjacent question. Monitoring answers "is the fleet
        // up", this answers "did the fix we shipped actually work".
        //
        // `requiresPermission`, NOT `adminOnly`. isItemVisible() reads adminOnly as
        // `role === 'admin'` exactly, and the role this page is built for is `developer` — the
        // only one granted monitoring.manage, and therefore the only one who can press the
        // fix-shipped button. With adminOnly the page would be reachable by URL and invisible in
        // the menu for precisely its intended user. The gate here now matches the route's
        // (monitoring.view), which is the only way a nav entry can be honest.
        { text: t ? t('Layout.menu_observability') : 'Observability', icon: <TroubleshootIcon />, path: '/observability', requiresPermission: monitoringViewPermission },
        { text: t ? t('Layout.menu_troubleshooting') : 'Troubleshooting', icon: <HelpIcon />, path: '/troubleshooting', requiresPermission: monitoringViewPermission },
        // Database server health (2026-09-18). requiresPermission carries the key AND the
        // company-1 check (see dbHealthPermission above).
        { text: t ? t('Layout.menu_database_health') : 'Database health', icon: <MonitorIcon />, path: '/database-health', requiresPermission: dbHealthPermission },
        { text: t ? t('Layout.menu_hub_status') : 'Hub Status', icon: <MonitorIcon />, path: '/hub-status', adminOnly: true },
        { text: t ? t('Layout.menu_hub_location_status') : 'Hub Location Status', icon: <MonitorIcon />, path: '/hub-location-status', adminOnly: true },
        { text: t ? t('Layout.menu_developer_info') : 'Developer Info', icon: <CodeIcon />, path: '/developer-info', adminOnly: true },
      ],
    },
  ];

  // Standalone bottom items (not in any group)
  const bottomStandaloneItems = [
    // Portal Ideabox (spec 20260817-portal-ideabox §8). Sits beside the Knowledge
    // Base: both are "help me with the product" surfaces rather than operational ones.
    // Gated on `ideas.view`, which every portal role holds by default — the people
    // who hit the friction are the ones who can report it.
    { text: t ? t('Layout.menu_ideas') : 'Ideas', icon: <LightbulbIcon />, path: '/ideas', requiresPermission: ideasPermission },
    { text: t ? t('Layout.menu_knowledge_base') : 'Knowledge Base', icon: <HelpIcon />, path: '/help' },
    { text: t ? t('Layout.menu_kb_admin') : 'KB Authoring', icon: <HelpIcon />, path: '/help/admin', requiresPermission: knowledgeBaseManagePermission },
  ];

  // The nav, flattened in lookup order (main menu, bottom groups, standalone) — the ONE
  // input of labelForNavPath for document.title, Recent pages and the feedback widget.
  function flatNavItems() {
    return [
      ...menuItems,
      ...bottomMenuGroups.flatMap((g) => g.items),
      ...bottomStandaloneItems,
    ];
  }
  const navItemsNow = flatNavItems();
  const navKey = navItemsNow.map((i) => `${i.path || ''}|${i.text || ''}`).join(';');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const navItems = React.useMemo(() => navItemsNow, [navKey]);

  // Which page is this — as a route PATTERN, never a path — published for the UI-feedback
  // widget and for pages that report their active tab (spec 20260925-ui-feedback §6.2).
  const currentPage = useCurrentPage({ navItems });

  // Collapse state with localStorage persistence
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('menuOpenGroups')) || {}; }
    catch { return {}; }
  });

  const toggleGroup = (id) => {
    setOpenGroups(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('menuOpenGroups', JSON.stringify(next));
      return next;
    });
  };

  // Auto-expand group containing the active route
  useEffect(() => {
    for (const group of bottomMenuGroups) {
      if (group.items.some(item => location.pathname.startsWith(item.path))) {
        if (!openGroups[group.id]) {
          setOpenGroups(prev => {
            const next = { ...prev, [group.id]: true };
            localStorage.setItem('menuOpenGroups', JSON.stringify(next));
            return next;
          });
        }
        break;
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Should this menu entry be shown?
   *
   * -- AN ITEM WITH NO GATE IS NOT AN UNGATED ITEM --------------------------
   *
   * This used to end in `return true`, so any entry nobody remembered to wire was
   * shown to everybody. Persons, Tickets, Notifications and Settings were all in that
   * state. Worse, the booleans for three of them WERE computed a few hundred lines up
   * (personsPermission / ticketsPermission / alarmsPermission) and then never passed to
   * an item -- dead variables that made the file look gated when it was not. A
   * dashboard-only user saw the whole menu and got "no permission" on arrival, which
   * reads as a broken portal rather than a deliberate restriction.
   *
   * The fallback now asks PAGE_REGISTRY, the same list the route guard in App.js is
   * audited against. One source, one answer, and a new page cannot be silently
   * visible-to-all: it either declares a permission there or it is deliberately
   * ungated and says so.
   *
   * Precedence is explicit-first, so nothing already decided by hand changes meaning:
   *   1. adminOnly            -- role === 'admin', unchanged
   *   2. requiresPermission   -- the value passed in, honoured either way
   *   3. the registry         -- the read-verb key for this path
   *   4. no registry entry    -- visible, and caught by the navRegistry test
   */
  const isItemVisible = (item) => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    if (item.requiresPermission !== undefined) return !!item.requiresPermission;
    const key = item.path ? visibilityPermissionForRoute(item.path) : null;
    // No key means public or deliberately ungated (Knowledge Base, hub status).
    if (!key) return true;
    return hasPermission(key);
  };

  // New-taxonomy sections (lazy — only computed when the flag is on).
  const newNavSections = React.useMemo(() => (
    newNav
      ? buildNewNavSections({
          t,
          permissionTemplatesPermission,
          locationTenancyPermission,
          cameraRequestsPermission,
          fieldInterventionsPermission,
          canManageUsers,
          canViewUsers,
          companyManagementPermission,
          knowledgeBaseManagePermission,
          shopPermission,
          invoicingConfigPermission,
          invoicingViewPermission,
          invoicingPaymentsPermission,
          posCatalogPermission,
          posRestrictionsPermission,
          posTerminalsPermission,
          posDayClosePermission,
          posReportsPermission,
          modulesManagePermission,
          modulesViewPermission,
          catalogManagePermission,
          estimatesPermission,
          ideasPermission,
          documentsPermission,
          documentsGrantsPermission,
          documentsExclusionsPermission,
          documentsRetentionPermission,
          documentsArchiveBadge,
          monitoringViewPermission,
          dbHealthPermission,
          // 20260819-registrationandonboarding. Both are PROTECTED-key gated, so they must
          // be threaded as permissions rather than adminOnly.
          organisationPermission,
          onboardingViewPermission,
        })
      : []
  ), [newNav, t, cameraRequestsPermission, fieldInterventionsPermission, canManageUsers, canViewUsers, companyManagementPermission, knowledgeBaseManagePermission, shopPermission, catalogManagePermission, estimatesPermission, ideasPermission, documentsPermission, documentsGrantsPermission, documentsExclusionsPermission, documentsRetentionPermission, documentsArchiveBadge, monitoringViewPermission, organisationPermission, onboardingViewPermission,
      // 20260818-invoicingsystem: these change when a module is enabled/disabled, so the
      // menu must recompute or a freshly-activated module stays invisible until reload.
      modulesManagePermission, modulesViewPermission, invoicingConfigPermission, invoicingViewPermission, invoicingPaymentsPermission, posCatalogPermission, posReportsPermission,
      posRestrictionsPermission, posTerminalsPermission, posDayClosePermission]);

  // Migrate saved open/closed state from the old group keys to the new
  // section ids exactly once, then remember we've done it. This keeps
  // the user's choice of "Integrations is expanded" intact after the
  // section was renamed to "Automation & Integrations".
  useEffect(() => {
    if (!newNav) return;
    try {
      if (localStorage.getItem('menuOpenGroupsVersion') === '2') return;
      const raw = localStorage.getItem('menuOpenGroups');
      const map = raw ? JSON.parse(raw) : {};
      const migrated = {};
      for (const [k, v] of Object.entries(map || {})) {
        const newKey = OLD_TO_NEW_GROUP_KEY[k] || k;
        migrated[newKey] = v;
      }
      localStorage.setItem('menuOpenGroups', JSON.stringify(migrated));
      localStorage.setItem('menuOpenGroupsVersion', '2');
      setOpenGroups(migrated);
    } catch { /* ignore */ }
  }, [newNav]);


  // When we're in rail mode, items render as centered icons with a
  // tooltip of their label. Groups collapse to a single icon button
  // that expands the drawer to wide mode on click (so the user can
  // see the children).
  const renderTopItem = (item, compact) => {
    const button = (
      <ListItemButton
        component={RouterLink}
        to={item.path}
        selected={location.pathname.startsWith(item.path)}
        onClick={handleNavClick}
        sx={compact ? { justifyContent: 'center', px: 1 } : undefined}
      >
        <ListItemIcon sx={compact ? { minWidth: 0, justifyContent: 'center' } : undefined}>
          {item.icon}
        </ListItemIcon>
        {!compact && <ListItemText primary={item.text} />}
        {!compact && item.badge && (
          <Chip
            label={item.badge}
            size="small"
            color={item.badgeColor || 'default'}
            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
          />
        )}
      </ListItemButton>
    );
    const content = (
      <ListItem key={item.text + item.path} disablePadding>
        {compact ? (
          <Tooltip title={item.text} placement="right" arrow>
            {button}
          </Tooltip>
        ) : button}
      </ListItem>
    );
    return content;
  };

  // `compact` is true when we're on the desktop rail (mobile drawer is
  // always wide because it's a temporary overlay).
  const drawerContent = (compact = false) => (
  <div>
       <Toolbar sx={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
          src="/logo/logo-full.png"
          alt={t ? t("Layout.caro_logo") : "CARO Logo"}
          style={{ height: compact ? 32 : 50, ...navLogoStyle(muiTheme) }} />

        </Box>
      </Toolbar>
      <Divider />
      {/* Legacy top-level flat list — hidden when the new taxonomy is active
          (every destination is housed inside a section there). */}
      {!newNav && (
        <List>
          {menuItems.
        filter(isItemVisible).
        map((item) => renderTopItem(item, compact))}
        </List>
      )}
      <Divider />
      <List component="nav" dense>
        {/* New taxonomy renders here when the flag is on; everything
            else (top-level flat list above, grouped items below) is
            skipped to avoid double-listing destinations. */}
        {newNav && newNavSections.map((section) => {
          const visibleItems = section.items.filter(isItemVisible);
          if (visibleItems.length === 0) return null;
          if (section.adminOnly && user?.role !== 'admin') return null;
          // Respect defaultOpen on first visit; after that honour the user's pick.
          const isOpen = (section.id in openGroups)
            ? openGroups[section.id]
            : !!section.defaultOpen;

          if (compact) {
            return (
              <Tooltip key={section.id} title={section.label} placement="right" arrow>
                <ListItemButton
                  onClick={() => {
                    toggleRail();
                    toggleGroup(section.id);
                  }}
                  sx={{ justifyContent: 'center', px: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                    {section.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            );
          }

          return (
            <React.Fragment key={section.id}>
              <ListItemButton onClick={() => toggleGroup(section.id)} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>{section.icon}</ListItemIcon>
                <ListItemText
                  primary={section.label}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', color: navSectionHeaderColor(muiTheme) }}
                />
                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {visibleItems.map((item) => (
                    <ListItemButton
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      selected={location.pathname.startsWith(item.path)}
                      sx={{ pl: 3 }}
                      onClick={handleNavClick}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {/* 5c (T-5c.14): a count on the icon, e.g. documents past their retention term. */}
                        {item.badge ? (
                          <Badge badgeContent={item.badge} color={item.badgeColor || 'error'} max={999} data-testid={`nav-badge-${item.path}`}>
                            {item.icon}
                          </Badge>
                        ) : item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}

        {/* Legacy structure — only rendered when the new-nav flag is OFF. */}
        {!newNav && bottomMenuGroups.map((group) => {
          // Filter visible items in this group based on per-item permissions.
          // The group is hidden when no items survive the filter — group-level
          // gating is no longer needed since each item carries its own
          // requiresPermission boolean (sourced from hasPermission()).
          const visibleItems = group.items.filter(isItemVisible);
          if (visibleItems.length === 0) return null;

          const isOpen = openGroups[group.id] || false;

          // On the rail, render each group as a single icon button that
          // expands the drawer on click (so the user can see children).
          if (compact) {
            return (
              <Tooltip key={group.id} title={group.label} placement="right" arrow>
                <ListItemButton
                  onClick={() => {
                    toggleRail();               // expand to wide
                    toggleGroup(group.id);      // open this group
                  }}
                  sx={{ justifyContent: 'center', px: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                    {group.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            );
          }

          return (
            <React.Fragment key={group.id}>
              <ListItemButton onClick={() => toggleGroup(group.id)} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {group.icon}
                </ListItemIcon>
                <ListItemText
                  primary={group.label}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                />
                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {visibleItems.map((item) => (
                    <ListItemButton
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      selected={location.pathname.startsWith(item.path)}
                      sx={{ pl: 4, py: 0.25 }}
                      onClick={handleNavClick}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{ variant: 'body2', fontSize: '0.82rem' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}

        {/* Standalone items (Help) — legacy nav only; new nav includes Help under System. */}
        {!newNav && bottomStandaloneItems.filter(isItemVisible).map((item) => renderTopItem(item, compact))}
      </List>

      {/* Rail-mode toggle at the bottom of the drawer */}
      <Box sx={{ mt: 'auto', p: 0.5, display: 'flex', justifyContent: compact ? 'center' : 'flex-end' }}>
        <Tooltip title={compact ? 'Expand sidebar' : 'Collapse to icons'} placement="right">
          <IconButton size="small" onClick={toggleRail} aria-label="Toggle sidebar width">
            {compact ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    </div>
  );


  return (
    <CurrentPageContext.Provider value={currentPage}>
    {/* Skip to content link for keyboard navigation */}
    <a href="#main-content" className="skip-to-content">{t ? t("Layout.skip_content") : "Skip to content"}

      </a>

    <Box sx={{ display: 'flex', ...(isImpersonating && { pt: '44px' }) }}>
      <AppBar
          position="fixed"
          sx={{
            width: { md: `calc(100% - ${currentDrawerWidth}px)` },
            ml: { md: `${currentDrawerWidth}px` },
            // Push the bar below the iOS status-bar / notch when viewport-fit=cover is active.
            pt: 'env(safe-area-inset-top, 0px)',
            transition: 'width 240ms ease, margin-left 240ms ease',
            ...(isImpersonating && { top: '44px' })
          }}>
          
        <Toolbar>
          <Tooltip title={t ? t("Layout.open_drawer") : 'Open navigation'}>
            <IconButton
                color="inherit"
                aria-label={t ? t("Layout.open_drawer") : "open drawer"}
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { md: 'none' } }}>
              <MenuIcon />
            </IconButton>
          </Tooltip>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find((item) => location.pathname.startsWith(item.path))?.text
              || bottomMenuGroups.flatMap(g => g.items).find((item) => location.pathname.startsWith(item.path))?.text
              || bottomStandaloneItems.find((item) => location.pathname.startsWith(item.path))?.text
              || 'Caro Management Controller'}
          </Typography>

          {/* Site Filter */}
          <SiteFilterSelector />

          {/* Dark/light mode quick toggle */}
          <Tooltip title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton
              color="inherit"
              onClick={() => setThemeMode(themeMode === 'dark' ? 'light' : 'dark')}
              aria-label="Toggle color theme"
              sx={{ ml: 0.5 }}
            >
              {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Notification Center */}
          <NotificationCenter />

          <Tooltip title={`${user?.username || 'Account'} — ${online ? 'online' : 'offline'}`}>
            <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-label="Open account menu"
                sx={{ p: 0, position: 'relative' }}>
              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                {user?.username?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              {/* Presence dot — green when online, grey when offline. */}
              <PresenceDot
                aria-hidden
                sx={{
                  position: 'absolute',
                  right: -2,
                  bottom: -2,
                  fontSize: 12,
                  // Literals are today's look; a theme may map them to its tokens via
                  // theme.caro.legacy.presence (Editorial does, plum/gold keep these).
                  color: online ? (presence?.online ?? '#2e7d32') : (presence?.offline ?? '#9e9e9e'),
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'background.paper',
                }}
              />
            </IconButton>
          </Tooltip>
          
          <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              
            <MenuItem disabled>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {user?.username || 'User'}
                <Chip
                    label={user?.role || 'User'}
                    size="small"
                    sx={{ ml: 1 }} />
                  
              </ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => navigate('/settings')}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t ? t("Layout.settings") : "Settings"}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { toggleNewNav(); setAnchorEl(null); }}>
              <ListItemIcon>
                <AccountTreeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={newNav ? 'Use classic sidebar' : 'Try new sidebar layout'}
                secondary={newNav ? 'Currently on: new taxonomy' : 'Preview: Monitor · Directory · Hardware · Automation · Org · System'}
                secondaryTypographyProps={{ sx: { fontSize: '0.72rem' } }}
              />
            </MenuItem>
            <MenuItem onClick={() => { setDensity(density === 'compact' ? 'comfortable' : 'compact'); setAnchorEl(null); }}>
              <ListItemIcon>
                {density === 'compact' ? <DensityComfortableIcon fontSize="small" /> : <DensityCompactIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={density === 'compact' ? 'Comfortable rows' : 'Compact rows'}
                secondary={density === 'compact' ? 'Currently: compact' : 'Currently: comfortable'}
                secondaryTypographyProps={{ sx: { fontSize: '0.72rem' } }}
              />
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t ? t("Layout.logout") : "Logout"}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
          component="nav"
          // When peeking, the nav footprint stays at `railWidth` so main
          // content doesn't jump; the drawer paper floats over it.
          sx={{
            width: { md: railMode ? railWidth : drawerWidthResolved },
            flexShrink: { md: 0 },
            transition: 'width 240ms ease'
          }}>

        <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            PaperProps={navPaperProps}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidthResolved }
            }}>
          {/* Mobile drawer always renders wide — it's a temporary overlay */}
          {drawerContent(false)}
        </Drawer>
        <Drawer
            variant="permanent"
            // Hover over the rail expands it temporarily; leave or click
            // collapses it back. Does nothing on coarse-pointer devices.
            onMouseEnter={openPeek}
            onMouseLeave={schedulePeekClose}
            PaperProps={navPaperProps}
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: currentDrawerWidth,
                // While dragging the resize handle we want snappy
                // tracking; otherwise keep the gentle glide.
                transition: resizing ? 'none' : 'width 240ms ease, box-shadow 240ms ease',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                // Raise a shadow while peeking so the drawer reads as
                // a floating sheet over the content, not a resize.
                boxShadow: peekOpen ? 4 : 0,
                zIndex: (theme) => peekOpen ? theme.zIndex.drawer + 1 : theme.zIndex.drawer,
              }
            }}
            open>
          {drawerContent(effectiveRail)}
          {/* Resize handle — visible when drawer is not in rail mode.
              Drag horizontally to resize, double-click to reset. */}
          {!effectiveRail && (
            <Box
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize sidebar"
              onMouseDown={() => setResizing(true)}
              onTouchStart={() => setResizing(true)}
              onDoubleClick={() => {
                setDrawerWidthResolved(defaultDrawerWidth);
                try { localStorage.setItem(WIDTH_PREF_KEY, String(defaultDrawerWidth)); } catch {}
              }}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 6,
                height: '100%',
                cursor: 'col-resize',
                zIndex: 2,
                transition: 'background-color 140ms ease',
                '&:hover, &:active': {
                  bgcolor: navResizeHoverColor(muiTheme),
                  opacity: 0.6,
                },
              }}
            />
          )}
        </Drawer>
      </Box>

      <Box
          component="main"
          id="main-content"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            // Reserve vertical room on mobile for the fixed bottom nav +
            // iOS home indicator. On md+ the drawer handles nav and no
            // extra padding is needed.
            pb: {
              xs: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px)`,
              md: 3,
            },
            // Width tracks the *footprint* (railWidth or drawerWidthResolved)
            // rather than `currentDrawerWidth`, so hover-peek doesn't
            // shove content sideways when it opens.
            width: { xs: '100%', md: `calc(100% - ${railMode ? railWidth : drawerWidthResolved}px)` },
            maxWidth: { xs: '100vw', md: `calc(100vw - ${railMode ? railWidth : drawerWidthResolved}px)` },
            transition: 'width 240ms ease, max-width 240ms ease',
            minHeight: '100vh',
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}>
          
        {/* Spacer matches AppBar height including safe-area-inset-top */}
        <Box sx={{ pt: 'env(safe-area-inset-top, 0px)' }}>
          <Toolbar />
        </Box>

        {/* System Notification Bar */}
        <NotificationBar />

        <Box
            sx={{
              flexGrow: 1,
              maxWidth: '100%',
              width: '100%',
              overflowX: 'hidden',
              boxSizing: 'border-box'
            }}>

          <PageTransition>
            <Outlet />
          </PageTransition>
        </Box>
        
        {/* Footer with version */}
        <Box
            component="footer"
            sx={{
              textAlign: 'center',
              py: 2,
              mt: 'auto',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
            
          <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.75rem',
                opacity: 0.6
              }}
              title={formatBuildDate()}>{APP_VERSION_LABEL}
            </Typography>
        </Box>
      </Box>

      {/* Mobile-only bottom quick-actions bar */}
      <MobileBottomNav />

      {/* Scroll-triggered FAB on long pages */}
      <BackToTopFab />

      {/* UI feedback (20260925-ui-feedback). Renders nothing without BOTH
          ideas.ui_feedback and ideas.create — closed by default. Sits above
          BackToTopFab when that one shows. */}
      <UiFeedbackWidget ref={feedbackRef} />

      {/* App-level command palette — Cmd/Ctrl+K */}
      <GlobalCommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={[
          // Page-registered context actions always surface first — they're
          // the most relevant for whatever the user is looking at right now.
          ...dynamicCommands.map((c) => ({
            ...c,
            group: c.group || 'This page',
          })),
          // Recent pages — at the top when nothing is typed. The palette's
          // query-based filter hides these as soon as the user starts typing.
          ...recentPages
            .filter((r) => r.path !== location.pathname)
            .slice(0, 5)
            .map((r) => ({
              label: r.label,
              path: r.path,
              icon: <HistoryIcon />,
              group: 'Recent',
              tags: ['recent', r.path],
              secondary: r.path,
            })),
          ...menuItems.filter(isItemVisible).map((item) => ({
            label: item.text,
            path: item.path,
            icon: item.icon,
            group: 'Navigate',
            tags: [item.path],
          })),
          ...bottomMenuGroups.flatMap((group) =>
            group.items.filter(isItemVisible).map((item) => ({
              label: item.text,
              path: item.path,
              icon: item.icon,
              group: group.label,
              tags: [item.path, group.label],
            }))
          ),
          ...bottomStandaloneItems.filter(isItemVisible).map((item) => ({
            label: item.text,
            path: item.path,
            icon: item.icon,
            group: 'Navigate',
            tags: [item.path],
          })),
          {
            label: themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
            icon: themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />,
            group: 'Preferences',
            tags: ['theme', 'color', 'appearance'],
            onRun: () => setThemeMode(themeMode === 'dark' ? 'light' : 'dark'),
          },
          {
            label: railMode ? 'Expand sidebar' : 'Collapse sidebar to icons',
            icon: railMode ? <ChevronRightIcon /> : <ChevronLeftIcon />,
            group: 'Preferences',
            tags: ['drawer', 'nav', 'rail'],
            onRun: toggleRail,
          },
          {
            label: 'Account & settings',
            icon: <SettingsIcon />,
            path: '/settings',
            group: 'Preferences',
            tags: ['settings', 'preferences'],
            shortcut: '⌘,',
          },
          {
            label: 'Show keyboard shortcuts',
            icon: <KeyboardIconImport />,
            group: 'Help',
            tags: ['help', 'shortcuts', 'cheatsheet'],
            shortcut: '?',
            onRun: () => cheatsheet.setOpen(true),
          },
          {
            label: 'Log out',
            icon: <LogoutIcon />,
            group: 'Session',
            tags: ['logout', 'signout'],
            onRun: handleLogout,
          },
        ]}
      />

      {/* Global keyboard cheatsheet — `?` opens it */}
      <ShortcutCheatsheet open={cheatsheet.open} onClose={() => cheatsheet.setOpen(false)} />
    </Box>
    </CurrentPageContext.Provider>);

};

export default Layout;